/**
 * EmailViewer — full-screen JARVIS email reader.
 *
 * Opens when an INTEL row is clicked. Pulls the full message via
 * /widgets/email/{id}, shows headers + body in HUD chrome, and
 * renders HTML in a sandboxed iframe (no scripts, no auto-load images).
 */
import { useEffect, useState } from 'react';
import { api } from '@/services/api';

interface FullMessage {
  id: string;
  from: string;
  to?: string;
  cc?: string;
  subject: string;
  date: string;
  body?: string;
  body_html?: string;
  snippet?: string;
}

interface Props {
  messageId: string;
  fallback: { subject: string; from: string; date?: string; snippet?: string };
  onClose: () => void;
}

export default function EmailViewer({ messageId, fallback, onClose }: Props) {
  const [msg, setMsg] = useState<FullMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showHtml, setShowHtml] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ message?: FullMessage; error?: string }>(
          `/widgets/email/${encodeURIComponent(messageId)}`,
        );
        if (cancelled) return;
        if (res.error) setError(res.error);
        else setMsg(res.message ?? null);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load message');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      cancelled = true;
      window.removeEventListener('keydown', onKey);
    };
  }, [messageId, onClose]);

  const subject = msg?.subject || fallback.subject;
  const from = msg?.from || fallback.from;
  const date = msg?.date || fallback.date || '';
  const html = msg?.body_html || '';
  const text = msg?.body || msg?.snippet || fallback.snippet || '';
  const hasHtml = Boolean(html);

  // Sandbox HTML — no scripts, no remote-by-default images. Wrap in a doc
  // with a CSP meta so the iframe inherits no parent privileges.
  const sandboxedHtml = hasHtml
    ? `<!doctype html><html><head><meta charset="utf-8" />
       <meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: data:; style-src 'unsafe-inline'; font-src https: data:;" />
       <base target="_blank">
       <style>
         body { background:#0a1320; color:#cbd5e1; font-family:-apple-system,system-ui,sans-serif; padding:18px; line-height:1.5; }
         a { color:#00d4ff; }
         img { max-width:100%; height:auto; }
         table { max-width:100%; }
         blockquote { border-left:2px solid rgba(0,212,255,0.3); padding-left:12px; color:#94a3b8; }
       </style></head><body>${html}</body></html>`
    : '';

  return (
    <div
      role="dialog"
      aria-modal
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9000,
        background: 'rgba(2, 6, 16, 0.86)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4vh 4vw',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass-panel"
        style={{
          width: '100%',
          maxWidth: 880,
          maxHeight: '92vh',
          background: 'linear-gradient(180deg, rgba(8,18,32,0.97), rgba(4,10,20,0.97))',
          border: '1px solid rgba(0,212,255,0.25)',
          boxShadow: '0 0 32px rgba(0,212,255,0.12)',
          display: 'flex',
          flexDirection: 'column',
          clipPath:
            'polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px)',
        }}
      >
        {/* Header chrome */}
        <div
          style={{
            padding: '14px 18px 10px',
            borderBottom: '1px solid rgba(0,212,255,0.12)',
          }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="text-[10px] font-mono tracking-[0.2em] text-jarvis-blue/70">
              INTEL · MESSAGE {msg?.id?.slice(-6).toUpperCase() ?? '······'}
            </div>
            <div className="flex items-center gap-2">
              {hasHtml && (
                <button
                  onClick={() => setShowHtml((v) => !v)}
                  className="text-[10px] font-mono px-2 py-0.5 border border-jarvis-blue/25 text-jarvis-blue/80 hover:bg-jarvis-blue/10"
                >
                  {showHtml ? 'PLAIN' : 'RICH'}
                </button>
              )}
              <button
                onClick={onClose}
                className="text-[10px] font-mono px-2 py-0.5 border border-white/15 text-gray-300 hover:bg-white/5"
              >
                CLOSE · ESC
              </button>
            </div>
          </div>
          <h2 className="mt-2 text-[15px] text-white font-display tracking-wide leading-snug">
            {subject || '(no subject)'}
          </h2>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] font-mono">
            <span className="text-gray-300">{from}</span>
            {msg?.to && <span className="text-gray-500">→ {msg.to}</span>}
            {date && <span className="text-gray-600">· {date}</span>}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, minHeight: 240, overflow: 'hidden' }}>
          {loading && (
            <div className="p-6 text-[11px] font-mono text-gray-500">
              FETCHING MESSAGE…
            </div>
          )}
          {!loading && error && (
            <div className="p-6 text-[11px] font-mono text-red-400">
              ERROR: {error}
            </div>
          )}
          {!loading && !error && hasHtml && showHtml && (
            <iframe
              title="email-body"
              sandbox="allow-popups"
              srcDoc={sandboxedHtml}
              style={{ width: '100%', height: '100%', border: 0, background: '#0a1320' }}
            />
          )}
          {!loading && !error && (!hasHtml || !showHtml) && (
            <pre
              style={{
                margin: 0,
                padding: '18px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: "'Science Gothic', sans-serif",
                fontSize: 13,
                lineHeight: 1.55,
                color: '#cbd5e1',
                height: '100%',
                overflowY: 'auto',
              }}
            >
              {text || '(no body)'}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
